<template>
  <div class="e-circular-history">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $t('eCircular.alleCircular') }}
          </span>
        </div>
        <div class="right-btns">
          <component
            :is="btn.component"
            v-for="(btn, index) in historyBtns"
            :key="`header-btn-${index}`"
            @click="btn.func()"
          >
            {{ $t(btn.title) }}
          </component>
        </div>
      </div>
    </sticky>
    <div class="all-list">
      <div class="history-content">
        <div
          class="search-conditions"
          @keydown.enter="search"
        >
          <div class="text-field condition small-3 medium-3 large-3 column">
            <label>
              {{ $i18n.t('eCircular.options.label') }}
            </label>
            <HKLSelect
              :options="eCircularOptions"
              :value="eCircularOptions[selectedOptionIndex].value"
              @change="updateECircularOption"
            />
          </div>
          <div class="text-field condition small-3 medium-3 large-3 condition column">
            <label for="">{{ $t('eCircular.email') }}</label>
            <div class="row text-input">
              <HKLInput
                v-model="searchAttributes.email"
                type="text"
                tabindex="0"
              />
            </div>
          </div>
          <div
            v-if="typeIsTenantOrTenancy"
            class="text-field condition small-3 medium-3 large-3 condition column"
          >
            <label for="">{{ $t('eCircular.tradeName') }}</label>
            <div class="row text-input">
              <HKLInput
                v-model="searchAttributes.tradeName"
                type="text"
                tabindex="1"
              />
            </div>
          </div>
          <div
            v-if="typeIsVendor"
            class="text-field condition small-3 medium-3 large-3 condition column"
          >
            <label for="">{{ $t('eCircular.companyName') }}</label>
            <div class="row text-input">
              <HKLInput
                v-model="searchAttributes.companyName"
                type="text"
                tabindex="2"
              />
            </div>
          </div>
        </div>
        <div class="total-count">
          <span>{{ $t('total') }}: {{ searchCount }}</span>
        </div>
        <HKLTable
          ref="table"
          :dataSource="lastSearchedECirculars"
          :columns="tableColumns"
          :filterBar="true"
          :onFilter="false"
          :sort="eCircularTableOrder.orderKey || 'updatedAt'"
          :defaultQueryObject="eCircularFilterConditions"
          @filter="onFilter"
          @loadmore="loadMore"
        />
      </div>
    </div>
  </div>
</template>

<script>

import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import HKLInput from '@/controls/Input/HKLInput'
import HKLCreateButton from '@/controls/Button/HKLCreateButton'
import HKLSearchButton from '@/controls/Button/HKLSearchButton'
import HKLTable from '@/controls/Table/HKLTable'
import Sticky from '@/controls/Sticky'
import * as ecircularAPI from '@/api/ecircular/request'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import HKLSelect from '@/controls/Select/HKLSelect'
import { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'

import { aclHelper, aclPermsType, tmsPermsTypes } from '@/utils/aclHelper'
import { E_CIRCULAR_TYPES, E_CIRCULAR_OPTIONS, E_CIRCULAR_STATUS, E_CIRCULAR_MARKETING_USER_TYPE_CODES } from './consts'
import {
  initialSearchAttributes,
  statusOps,
  getUserTypeFilterListModeFromTenancyAccess,
} from './helper'
import CmsModulesStatusMixin from '@/modules/CmsModulesStatusMixin'
import TmsModulesStatusMixin from '@/modules/TmsModulesStatusMixin'
import ECircularColumnsMixin from './ECircularColumnsMixin'

/**
 * @type {import('vue').Component}
 */
export default {
  name: 'ECircularHistory',
  components: {
    HKLTable,
    Sticky,
    HKLInput,
    HKLSelect,
  },
  mixins: [CmsModulesStatusMixin, TmsModulesStatusMixin, ECircularColumnsMixin],
  data () {
    return {
      E_CIRCULAR_OPTIONS,
      isEditing: true,
      searchBtn: {
        title: 'eCircular.search',
        func: this.search,
        component: HKLSearchButton,
      },
      createBtn: {
        title: 'eCircular.createECircular',
        func: this.createECircularWithCurrentType,
        component: HKLCreateButton,
      },
      searchAttributes: {
        ...initialSearchAttributes,
        type: (this.$router.currentRoute.params.type || E_CIRCULAR_TYPES.TENANT).toUpperCase(),
      },
      interactionFunc: {
        code: this.gotoECircularDetail,
        subject: this.gotoECircularDetail,
      },
      totalLength: 0,
      filterObject: lodash.cloneDeep(this.$store.getters['ecircular/eCircularFilterConditions'] || {}),
    }
  },
  computed: {
    ...mapGetters('ecircular', ['lastECircularSearchAttrs', 'lastSearchedECirculars', 'searchCount', 'eCircularFilterConditions', 'eCircularTableOrder', 'categories']),
    ...mapGetters('user', ['buildings', 'currentUser', 'acTenancyIsOffice', 'acTenancyIsRetail']),
    ...mapGetters('categories', {
      rawCategoryOptions: 'categoryOptions',
    }),
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate) || aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)
    },
    canRead () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.read) || aclHelper.getAccess(aclPermsType.vendorECircular.read)
    },
    userTypeFilterListMode () {
      const raw = lodash.get(this.currentUser, 'departments', [])
      const departments = Array.isArray(raw) ? raw : []
      const shortnames = departments.map((d) =>
        lodash.toUpper(String(lodash.get(d, 'shortname', '')).trim()),
      )
      const isMkt = shortnames.includes('MC')
      const isPm = shortnames.includes('PM')
      return getUserTypeFilterListModeFromTenancyAccess(isMkt, isPm)
    },
    eCircularOptions () {
      let options = []
      this.enableTms && aclHelper.getAccess(tmsPermsTypes.tenantEcircular.read) && options.push(E_CIRCULAR_OPTIONS.find(item => item.value === E_CIRCULAR_TYPES.TENANT))
      this.enableCms && aclHelper.getAccess(aclPermsType.vendorECircular.read) && options.push(E_CIRCULAR_OPTIONS.find(item => item.value === E_CIRCULAR_TYPES.VENDOR))

      return options.length ? options.map(item => {
        return Object.assign({}, item, { name: this.$t(`eCircular.options.${item.value.toLowerCase()}`) })
      }) : []
    },
    historyBtns () {
      return this.canCreateUpdate ? [this.createBtn, this.searchBtn] : [this.searchBtn]
    },
    selectedOptionIndex () {
      return this.eCircularOptions.findIndex(option => option.value === this.searchAttributes.type)
    },
    typeIsTenantOrTenancy () {
      return this.searchAttributes.type === E_CIRCULAR_TYPES.TENANT || this.searchAttributes.type === E_CIRCULAR_TYPES.TENANCY
    },
    typeIsVendor () {
      return this.searchAttributes.type === E_CIRCULAR_TYPES.VENDOR
    },
    showLoadMore () {
      return this.lastSearchedECirculars.length > 0 && this.lastSearchedECirculars.length < this.searchCount
    },
    categoryOptions () {
      return this.rawCategoryOptions.reduce((options, group) => {
        const opts = group.options.filter((opt) => opt.type === this.searchAttributes.type)
        if (opts.length > 0) {
          options.push({
            ...group,
            options: opts,
          })
        }
        return options
      }, [])
    },
    tableColumns () {
      const columns = lodash.cloneDeep(this.eCircularColumnsMap[this.searchAttributes.type])
      return columns.map((column) => {
        if (Object.keys(this.interactionFunc).includes(column.dataIndex)) {
          column.component = Object.assign({}, column.component, {
            listeners: (_, record) => ({
              click: () => {
                const ref = this.getECircularByCode(record.code)
                this.getAllowLink(ref) && this.interactionFunc[column.dataIndex](ref)
              },
            }),
          })
        }
        if (['code', 'subject'].includes(column.dataIndex)) {
          const componentObj = lodash.cloneDeep(column.component)
          column.component.name = (_, record) => {
            const component = lodash.cloneDeep(componentObj)
            return this.getAllowLink(this.getECircularByCode(record.code)) && component.name
          }
          column.component.attrs = (_, record) => {
            const component = lodash.cloneDeep(componentObj)
            if (!this.getAllowLink(this.getECircularByCode(record.code))) {
              delete component.attrs.class
            }
            return component.attrs
          }
        }
        return column
      })
    },
  },
  watch: {
    $route () {
      this.syncTypeFromRouteParam()
    },
  },
  methods: {
    ...mapActions('buildings', ['initBuildingOptions']),
    ...mapActions('ecircular', ['createECircular', 'setECircularSearchResults']),
    ...mapMutations('ecircular', {
      updateSearchCount: mutationTypes.ECIRCULAR_SAVE_SEARCH_COUNT,
      updateFilterConditions: mutationTypes.ECIRCULAR_SAVE_ECIRCULAR_FILTER_CONDITIONS,
      updateTableOrder: mutationTypes.ECIRCULAR_SAVE_ECIRCULAR_TABLE_ORDER,
    }),
    onFilter (filterObject) {
      this.filterObject = filterObject
      this.updateFilterConditions(lodash.cloneDeep(filterObject))
    },
    createECircularWithCurrentType () {
      this.createECircular({type: this.searchAttributes.type})
    },
    resetECircularType (type) {
      this.resetSearchAttributes({type})
      this.setECircularSearchResults({
        lastECircularSearchAttrs: lodash.cloneDeep(this.searchAttributes),
        lastSearchedECirculars: [],
      })
      this.updateSearchCount(0)
    },
    resetSearchAttributes (attrs) {
      // reset/initialize all values
      for (const key in initialSearchAttributes) this.searchAttributes[key] = initialSearchAttributes[key]
      // assign new values
      for (const key in attrs) this.searchAttributes[key] = attrs[key]
    },
    updateECircularOption (selectedOptionVaule) {
      if (this.searchAttributes.type === selectedOptionVaule) return

      this.resetECircularType(selectedOptionVaule)
      this.syncTypeToRouteParam()
      this.resetTypeSpecificTableFilters()
    },
    syncTypeFromRouteParam () {
      const newECircularType = (this.$router.currentRoute.params.type || E_CIRCULAR_TYPES.TENANT).toUpperCase()
      if (this.searchAttributes.type === newECircularType) return

      this.resetECircularType(newECircularType)
      this.resetTypeSpecificTableFilters()
    },
    resetTypeSpecificTableFilters () {
      const filterObject = {
        ...lodash.cloneDeep(this.filterObject),
        category: [],
        userType: [],
      }
      this.filterObject = filterObject
      if (this.$refs.table) {
        this.$refs.table.queryObject = lodash.cloneDeep(filterObject)
      }
      this.updateFilterConditions(lodash.cloneDeep(filterObject))
    },
    syncTypeToRouteParam () {
      const type = this.searchAttributes.type.toLowerCase()
      if (this.$router.currentRoute.params.type === type) return

      this.$router.replace({ params: { type } })
    },
    getFilterParams () {
      const obj = { ...this.searchAttributes, ...this.filterObject }
      if (obj.category) {
        const hasAll = obj.category.includes(ALL_VALUE)
        if (hasAll) {
          obj.category = []
        }
      }
      if (obj.status) {
        const hasAll = obj.status.includes(ALL_VALUE)
        if (hasAll) {
          obj.status = Object.values(statusOps)
        }
      }
      const userTypeFilterKeys = obj.userType
      delete obj.userType
      if (userTypeFilterKeys && userTypeFilterKeys.length) {
        const normalizedUserTypeFilterKeys = userTypeFilterKeys
          .map((item) => {
            if (typeof item === 'string') return item
            if (item && typeof item === 'object') {
              if (typeof item.value === 'string') return item.value
              if (typeof item.key === 'string') return item.key
            }
            return null
          })
          .filter(Boolean)
        if (obj.type === E_CIRCULAR_TYPES.TENANT) {
          const ut = { isCircular: false, isFireMarshal: false }
          const marketingRoles = []
          normalizedUserTypeFilterKeys.forEach((key) => {
            if (key === 'isCircular') ut.isCircular = true
            else if (key === 'isFireMarshal') ut.isFireMarshal = true
            else if (typeof key === 'string' && E_CIRCULAR_MARKETING_USER_TYPE_CODES.includes(key)) {
              marketingRoles.push(key)
            }
          })
          if (marketingRoles.length) ut.marketingRoles = marketingRoles
          obj.userContactTypes = JSON.stringify(ut)
        } else if (obj.type === E_CIRCULAR_TYPES.VENDOR) {
          obj.userContactTypes = JSON.stringify({ vendorTypes: normalizedUserTypeFilterKeys })
        }
      }
      return obj
    },
    async search () {
      this.$refs.table.scrollToTableTop()
      this.searchNext(1)
    },
    async searchNext (page) {
      this.searchAttributes.page = page
      this.searchAttributes.offset = (page - 1) * this.searchAttributes.limit
      const response = await ecircularAPI.getAllECircular(this.getFilterParams())
      this.updateSearchCount(response.count)
      let lastData = page === 1 ? [] : this.lastSearchedECirculars
      lastData = lodash.unionBy(lastData, response.data, 'id')
      this.setECircularSearchResults({
        lastECircularSearchAttrs: lodash.cloneDeep(this.searchAttributes),
        lastSearchedECirculars: lastData,
      })
    },
    gotoECircularDetail (ecircular) {
      if (ecircular.status === E_CIRCULAR_STATUS.DRAFT && this.canCreateUpdate) {
        navigation.toPage('createECircular', { id: ecircular.id, createOrEdit: 'edit' })
      } else {
        navigation.toPage('eCircularDetail', { id: ecircular.id })
      }
    },
    getDisplayListLength (length) {
      this.totalLength = length
    },
    async loadMore () {
      if (!this.showLoadMore) return
      this.searchNext(this.searchAttributes.page + 1)
    },
    getTableOrder (orderKey, order) {
      this.updateTableOrder({orderKey, order})
    },
    afterResizeTable (isWin, hasVerticalScrollbar) {
      if (!isWin || !hasVerticalScrollbar) return

      const currentTableContentElm = lodash.get(this, `$refs.commonTable.$refs.currentTableContent.$el`)
      if (!currentTableContentElm) return

      const $subject = $(currentTableContentElm).find(`.subject`)
      if ($subject[0]) return

      const width = $subject[0].getBoundingClientRect().width
      if (!width) return

      $subject.css('width', width - 17)
    },
    getAllowLink (eCircular) {
      const self = this
      const isCreator = lodash.get(eCircular, 'creator.id') === this.currentUser.id

      const conds = {
        // TODO Should be removed After R4. Because the non-DRAFT status of E-circular must have buildings
        get noBuilding () {
          return !lodash.get(eCircular, 'buildings.length')
        },
        get hasBuildingAccess () {
          return (self.buildings || []).some(
            building => eCircular.buildings.includes(building),
          )
        },
        get notDraft () {
          return eCircular.status !== E_CIRCULAR_STATUS.DRAFT
        },
        get allowedStatus () {
          return [E_CIRCULAR_STATUS.SENT, E_CIRCULAR_STATUS.RESENT].includes(eCircular.status)
        },
      }

      const ref = (isCreator && this.canCreateUpdate) ||
        (conds.notDraft && this.canRead && (conds.noBuilding || conds.hasBuildingAccess)) ||
        conds.allowedStatus
      return !!ref
    },
    getECircularByCode (code) {
      return this.lastSearchedECirculars.find((e) => code === e.code)
    },
  },
  /* eslint-disable vue/order-in-components */
  async mounted () {
    await this.initBuildingOptions()
    if (Object.keys(this.lastECircularSearchAttrs).length <= 0) return

    this.resetSearchAttributes({...this.lastECircularSearchAttrs})
    this.syncTypeToRouteParam()
  },
}
</script>
<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang='scss' scoped>
@import '~styles/e-circular/history';
@import '~styles/textField';
.e-circular-history {
  display: flex;
  flex-direction: column;
  .header {
    .notice-header {
      line-height: 40px;
    }
  }
  .all-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    .history-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
  ::v-deep .table-list {
    display: block;
  }
  .hkl-table {
    height: calc(100vh - 300px);

    & ::v-deep .underline {
      text-decoration: underline;
      cursor: pointer;
    }
  }

}
</style>
